import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface ColorDotProps extends Omit<BoxProps, 'color'> {
  color: string
  size?: number
  selected?: boolean
  label?: string
}

const Dot = styled(Box, {
  shouldForwardProp: (p) => p !== 'dotColor' && p !== 'dotSize' && p !== 'selected',
})<{ dotColor: string; dotSize: number; selected?: boolean }>(
  ({ dotColor, dotSize, selected }) => ({
    width: dotSize,
    height: dotSize,
    borderRadius: 'var(--ds-radius-pill)',
    backgroundColor: dotColor,
    cursor: 'pointer',
    flexShrink: 0,
    boxSizing: 'border-box',
    border: '1px solid rgba(17,17,17,0.08)',
    outline: selected ? '2px solid var(--ds-ink)' : '2px solid transparent',
    outlineOffset: 3,
    boxShadow: selected ? 'var(--ds-shadow-soft)' : 'none',
    transition: [
      'transform var(--ds-duration-fast) var(--ds-ease)',
      'outline-color var(--ds-duration-fast) var(--ds-ease)',
      'box-shadow var(--ds-duration-fast) var(--ds-ease)',
    ].join(', '),
    '&:hover': { transform: 'scale(1.08)' },
    '&:focus-visible': {
      outline: '2px solid var(--ds-ink)',
      outlineOffset: 4,
    },
  }),
)

/** Theme accent / filter / swatch dot */
export function ColorDot({ color, size = 14, selected, label, onClick, ...rest }: ColorDotProps) {
  return (
    <Dot
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={label}
      aria-pressed={selected}
      dotColor={color}
      dotSize={size}
      selected={selected}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick(e as unknown as React.MouseEvent<HTMLDivElement>)
              }
            }
          : undefined
      }
      {...rest}
    />
  )
}
